<template>
  <div class="aarrr-metrics-section">
    <h2 class="section-title">AARRR 메트릭 설정</h2>
    <p class="section-description">이벤트의 성과를 측정할 AARRR 메트릭을 설정하세요</p>

    <a-row :gutter="16" class="metrics-container">
      <!-- 왼쪽: 사용 가능한 액티비티 목록 -->
      <a-col :span="8" class="activity-panel">
        <a-card title="사용 가능한 액티비티" size="small">
          <template #extra>
            <a-button @click="loadActivityTypes" :loading="loadingActivities" size="small">
              <reload-outlined /> 새로고침
            </a-button>
          </template>

          <a-input-search
            v-model:value="searchKeyword"
            placeholder="액티비티 검색..."
            style="margin-bottom: 16px"
          />

          <div class="activity-list">
            <a-empty v-if="filteredActivities.length === 0" description="액티비티가 없습니다" />

            <div
              v-for="activity in filteredActivities"
              :key="activity.type"
              class="activity-item"
              draggable="true"
              @dragstart="handleDragStart(activity)"
              @dragend="handleDragEnd"
            >
              <div class="activity-header">
                <drag-outlined class="drag-icon" />
                <span class="activity-type">{{ activity.type }}</span>
              </div>
              <div class="activity-meta">
                <span class="count">사용: {{ activity.count }}회</span>
                <span class="last-used">{{ formatDate(activity.lastUsed) }}</span>
              </div>
            </div>
          </div>
        </a-card>
      </a-col>

      <!-- 오른쪽: AARRR 단계별 드롭존 -->
      <a-col :span="16" class="metrics-panel">
        <a-card>
          <a-tabs v-model:activeKey="activeTab">
            <a-tab-pane v-for="stage in aarrrStages" :key="stage.key" :tab="stage.label">
              <template #tab>
                <span>
                  {{ stage.label }}
                  <a-badge :count="getStageMetricsCount(stage.key)" :offset="[10, 0]" />
                </span>
              </template>

              <div class="stage-description">
                <info-circle-outlined /> {{ stage.description }}
              </div>

              <div
                class="drop-zone"
                :class="{ 'drag-over': dragOverStage === stage.key }"
                @dragover.prevent="handleDragOver(stage.key)"
                @dragleave="handleDragLeave"
                @drop="handleDrop(stage.key)"
              >
                <div v-if="getStageMetrics(stage.key).length === 0" class="empty-state">
                  <inbox-outlined style="font-size: 48px; color: #d9d9d9;" />
                  <p>액티비티를 여기로 드래그하세요</p>
                </div>

                <div v-else class="metrics-list">
                  <div
                    v-for="(metric, index) in getStageMetrics(stage.key)"
                    :key="`${stage.key}-${index}`"
                    class="metric-item"
                  >
                    <div class="metric-content">
                      <div class="metric-header">
                        <span class="metric-type">{{ metric.activityType }}</span>
                        <a-button-group size="small">
                          <a-button @click="editMetric(stage.key, index)">
                            <edit-outlined />
                          </a-button>
                          <a-button danger @click="removeMetric(stage.key, index)">
                            <delete-outlined />
                          </a-button>
                        </a-button-group>
                      </div>

                      <div class="metric-details">
                        <a-tag v-if="metric.displayName" color="blue">
                          {{ metric.displayName }}
                        </a-tag>
                        <a-tag v-if="metric.isConversion" color="green">
                          전환 이벤트
                        </a-tag>
                        <a-tag v-if="metric.weight && metric.weight !== 1">
                          가중치: {{ metric.weight }}
                        </a-tag>
                        <a-tag v-if="metric.conversionValue" color="gold">
                          ₩{{ metric.conversionValue.toLocaleString() }}
                        </a-tag>
                        <a-tag v-if="metric.target">
                          목표: {{ metric.target }}
                        </a-tag>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a-tab-pane>
          </a-tabs>
        </a-card>
      </a-col>
    </a-row>

    <!-- 메트릭 편집 드로워 -->
    <a-drawer
      v-model:visible="editDrawerVisible"
      title="메트릭 상세 설정"
      :width="600"
      placement="right"
      :destroyOnClose="true"
      @close="cancelEdit"
    >
      <a-form :model="editingMetric" layout="vertical">
        <a-form-item label="액티비티 타입">
          <a-input :value="editingMetric.activityType" disabled />
        </a-form-item>

        <a-form-item label="표시명">
          <a-input
            v-model:value="editingMetric.displayName"
            placeholder="UI에 표시될 이름 (선택사항)"
          />
        </a-form-item>

        <a-form-item label="전환 설정">
          <a-checkbox v-model:checked="editingMetric.isConversion">
            전환 이벤트로 설정
          </a-checkbox>
        </a-form-item>

        <a-form-item label="가중치" v-if="editingMetric.isConversion">
          <a-slider
            v-model:value="editingMetric.weight"
            :min="1"
            :max="10"
            :marks="{ 1: '1', 5: '5', 10: '10' }"
          />
        </a-form-item>

        <a-form-item label="전환 가치 (원)" v-if="editingMetric.isConversion">
          <a-input-number
            v-model:value="editingMetric.conversionValue"
            :min="0"
            :step="1000"
            :formatter="value => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="value => value.replace(/₩\s?|(,*)/g, '')"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item label="목표값">
          <a-input-number
            v-model:value="editingMetric.target"
            :min="0"
            placeholder="목표 달성 수 (선택사항)"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item label="퍼널 단계">
          <a-input-number
            v-model:value="editingMetric.funnelStep"
            :min="1"
            placeholder="퍼널 분석 시 순서 (선택사항)"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item label="달성 조건">
          <a-space direction="vertical" style="width: 100%">
            <a-input-number
              v-model:value="editingMetric.conditions.minCount"
              :min="0"
              placeholder="최소 횟수"
              style="width: 100%"
            >
              <template #addonBefore>최소</template>
              <template #addonAfter>회</template>
            </a-input-number>

            <a-input-number
              v-model:value="editingMetric.conditions.maxCount"
              :min="0"
              placeholder="최대 횟수"
              style="width: 100%"
            >
              <template #addonBefore>최대</template>
              <template #addonAfter>회</template>
            </a-input-number>

            <a-input-number
              v-model:value="editingMetric.conditions.timeWindow"
              :min="0"
              placeholder="시간 제한 (초)"
              style="width: 100%"
            >
              <template #addonBefore>시간</template>
              <template #addonAfter>초</template>
            </a-input-number>
          </a-space>
        </a-form-item>

        <a-form-item>
          <a-checkbox v-model:checked="editingMetric.isActive">
            활성화
          </a-checkbox>
        </a-form-item>
      </a-form>

      <!-- 드로워 하단 버튼 -->
      <div class="drawer-footer">
        <a-space>
          <a-button @click="cancelEdit">취소</a-button>
          <a-button type="primary" @click="saveMetricDetails" class="yellow-button">저장</a-button>
        </a-space>
      </div>
    </a-drawer>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import {
  DragOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  InboxOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';

export default {
  name: 'AARRRMetricsSection',
  components: {
    DragOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    InboxOutlined,
    EditOutlined,
    DeleteOutlined
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    eventId: {
      type: String,
      default: ''
    },
    api: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    // AARRR 단계 정의
    const aarrrStages = [
      {
        key: 'acquisition',
        label: 'Acquisition (획득)',
        description: '사용자가 서비스를 인지하고 방문하는 단계'
      },
      {
        key: 'activation',
        label: 'Activation (활성화)',
        description: '사용자가 첫 번째 핵심 경험을 하는 단계'
      },
      {
        key: 'retention',
        label: 'Retention (유지)',
        description: '사용자가 재방문하고 지속적으로 사용하는 단계'
      },
      {
        key: 'revenue',
        label: 'Revenue (수익)',
        description: '사용자가 수익을 창출하는 단계'
      },
      {
        key: 'referral',
        label: 'Referral (추천)',
        description: '사용자가 다른 사용자를 추천하는 단계'
      }
    ];

    // State
    const activeTab = ref('acquisition');
    const searchKeyword = ref('');
    const activities = ref([]);
    const loadingActivities = ref(false);
    const draggedActivity = ref(null);
    const dragOverStage = ref(null);
    const editDrawerVisible = ref(false);
    const editingMetric = ref({
      conditions: {
        minCount: null,
        maxCount: null,
        timeWindow: null
      }
    });
    const editingStage = ref('');
    const editingIndex = ref(-1);

    // form.aarrrMetrics 초기화
    if (!props.form.aarrrMetrics) {
      props.form.aarrrMetrics = {
        acquisition: [],
        activation: [],
        retention: [],
        revenue: [],
        referral: []
      };
    }

    // 필터링된 액티비티 목록
    const filteredActivities = computed(() => {
      if (!searchKeyword.value) return activities.value;

      const keyword = searchKeyword.value.toLowerCase();
      return activities.value.filter(activity =>
        activity.type.toLowerCase().includes(keyword)
      );
    });

    // 액티비티 타입 로드
    const loadActivityTypes = async () => {
      // 이벤트 ID가 없으면 샘플 데이터 사용
      if (!props.eventId) {
        // 샘플 데이터
        activities.value = [
          { type: 'page_view', count: 1500, lastUsed: new Date().toISOString() },
          { type: 'landing', count: 1200, lastUsed: new Date().toISOString() },
          { type: 'sign_up', count: 800, lastUsed: new Date().toISOString() },
          { type: 'share', count: 600, lastUsed: new Date().toISOString() },
          { type: 'purchase', count: 400, lastUsed: new Date().toISOString() },
          { type: 'review_write', count: 300, lastUsed: new Date().toISOString() },
          { type: 'app_download', count: 250, lastUsed: new Date().toISOString() },
          { type: 'referral', count: 150, lastUsed: new Date().toISOString() },
          { type: 'coupon_use', count: 100, lastUsed: new Date().toISOString() }
        ];
        message.info('샘플 액티비티 데이터를 사용합니다');
        return;
      }

      if (!props.api || !props.api.getEventActivityTypes) {
        console.warn('API 인스턴스가 없어 샘플 데이터를 사용합니다');
        // API가 없으면 샘플 데이터 사용
        activities.value = [
          { type: 'page_view', count: 1500, lastUsed: new Date().toISOString() },
          { type: 'landing', count: 1200, lastUsed: new Date().toISOString() },
          { type: 'sign_up', count: 800, lastUsed: new Date().toISOString() },
          { type: 'share', count: 600, lastUsed: new Date().toISOString() },
          { type: 'purchase', count: 400, lastUsed: new Date().toISOString() }
        ];
        return;
      }

      loadingActivities.value = true;
      try {
        await props.api.getEventActivityTypes(
          props.eventId,
          (data) => {
            activities.value = data.activityTypes || [];
            message.success(`${activities.value.length}개의 액티비티 타입을 불러왔습니다`);
          },
          (error) => {
            console.error('액티비티 타입 로드 실패:', error);
            message.error('액티비티 타입을 불러오는데 실패했습니다');
          }
        );
      } finally {
        loadingActivities.value = false;
      }
    };

    // 드래그 시작
    const handleDragStart = (activity) => {
      draggedActivity.value = activity;
    };

    // 드래그 종료
    const handleDragEnd = () => {
      draggedActivity.value = null;
      dragOverStage.value = null;
    };

    // 드래그 오버
    const handleDragOver = (stage) => {
      dragOverStage.value = stage;
    };

    // 드래그 떠남
    const handleDragLeave = () => {
      dragOverStage.value = null;
    };

    // 드롭
    const handleDrop = (stage) => {
      if (!draggedActivity.value) return;

      const newMetric = {
        activityType: draggedActivity.value.type,
        displayName: '',
        isConversion: false,
        weight: 1,
        conversionValue: 0,
        target: null,
        funnelStep: null,
        conditions: {
          minCount: null,
          maxCount: null,
          timeWindow: null
        },
        isActive: true
      };

      if (!props.form.aarrrMetrics[stage]) {
        props.form.aarrrMetrics[stage] = [];
      }

      props.form.aarrrMetrics[stage].push(newMetric);
      message.success(`${draggedActivity.value.type}이(가) ${stage}에 추가되었습니다`);

      draggedActivity.value = null;
      dragOverStage.value = null;
    };

    // 특정 단계의 메트릭 가져오기
    const getStageMetrics = (stage) => {
      return props.form.aarrrMetrics[stage] || [];
    };

    // 특정 단계의 메트릭 개수
    const getStageMetricsCount = (stage) => {
      return (props.form.aarrrMetrics[stage] || []).length;
    };

    // 메트릭 편집
    const editMetric = (stage, index) => {
      const metric = props.form.aarrrMetrics[stage][index];
      editingMetric.value = {
        ...metric,
        conditions: metric.conditions || {
          minCount: null,
          maxCount: null,
          timeWindow: null
        }
      };
      editingStage.value = stage;
      editingIndex.value = index;
      editDrawerVisible.value = true;
    };

    // 메트릭 삭제
    const removeMetric = (stage, index) => {
      props.form.aarrrMetrics[stage].splice(index, 1);
      message.success('메트릭이 삭제되었습니다');
    };

    // 메트릭 상세 저장
    const saveMetricDetails = () => {
      // 빈 값 정리
      const cleanedMetric = { ...editingMetric.value };

      // conditions 정리
      if (cleanedMetric.conditions) {
        Object.keys(cleanedMetric.conditions).forEach(key => {
          if (!cleanedMetric.conditions[key]) {
            delete cleanedMetric.conditions[key];
          }
        });
        if (Object.keys(cleanedMetric.conditions).length === 0) {
          delete cleanedMetric.conditions;
        }
      }

      // null 값 정리
      Object.keys(cleanedMetric).forEach(key => {
        if (cleanedMetric[key] === null || cleanedMetric[key] === '') {
          if (key !== 'activityType' && key !== 'isConversion' && key !== 'isActive') {
            delete cleanedMetric[key];
          }
        }
      });

      props.form.aarrrMetrics[editingStage.value][editingIndex.value] = cleanedMetric;
      editDrawerVisible.value = false;
      message.success('메트릭 설정이 저장되었습니다');
    };

    // 편집 취소
    const cancelEdit = () => {
      editDrawerVisible.value = false;
      // conditions가 undefined가 되지 않도록 초기값 설정
      editingMetric.value = {
        conditions: {
          minCount: null,
          maxCount: null,
          timeWindow: null
        }
      };
    };

    // 날짜 포맷
    const formatDate = (date) => {
      if (!date) return '';
      return dayjs(date).format('MM/DD HH:mm');
    };

    // 이벤트 ID가 변경되면 액티비티 타입 다시 로드
    watch(() => props.eventId, (newId) => {
      if (newId && props.api) {
        loadActivityTypes();
      }
    });

    // API가 변경되면 액티비티 타입 다시 로드
    watch(() => props.api, (newApi) => {
      if (newApi && props.eventId) {
        loadActivityTypes();
      }
    });

    onMounted(() => {
      // 항상 액티비티 타입을 로드 (샘플 데이터 또는 실제 데이터)
      loadActivityTypes();
    });

    return {
      aarrrStages,
      activeTab,
      searchKeyword,
      activities,
      loadingActivities,
      filteredActivities,
      dragOverStage,
      editDrawerVisible,
      editingMetric,
      loadActivityTypes,
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      getStageMetrics,
      getStageMetricsCount,
      editMetric,
      removeMetric,
      saveMetricDetails,
      cancelEdit,
      formatDate
    };
  }
};
</script>

<style lang="scss" scoped>
.aarrr-metrics-section {
  padding: 24px;

  .section-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .section-description {
    color: #666;
    margin-bottom: 24px;
  }

  .metrics-container {
    min-height: 600px;
  }

  .activity-panel {
    .activity-list {
      max-height: 500px;
      overflow-y: auto;
    }

    .activity-item {
      padding: 12px;
      margin-bottom: 8px;
      background: #f5f5f5;
      border-radius: 4px;
      cursor: move;
      transition: all 0.3s;

      &:hover {
        background: #e6f7ff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .activity-header {
        display: flex;
        align-items: center;
        margin-bottom: 4px;

        .drag-icon {
          margin-right: 8px;
          color: #999;
        }

        .activity-type {
          font-weight: 500;
          flex: 1;
        }
      }

      .activity-meta {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #999;

        .count {
          color: #1890ff;
        }
      }
    }
  }

  .metrics-panel {
    .stage-description {
      padding: 12px;
      background: #f0f2f5;
      border-radius: 4px;
      margin-bottom: 16px;
      color: #666;
      font-size: 14px;
    }

    .drop-zone {
      min-height: 300px;
      border: 2px dashed #d9d9d9;
      border-radius: 4px;
      padding: 16px;
      transition: all 0.3s;

      &.drag-over {
        border-color: #1890ff;
        background: #f0f8ff;
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #999;
      }

      .metrics-list {
        .metric-item {
          padding: 12px;
          background: white;
          border: 1px solid #e8e8e8;
          border-radius: 4px;
          margin-bottom: 8px;

          .metric-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;

            .metric-type {
              font-weight: 500;
            }
          }

          .metric-details {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }
        }
      }
    }
  }

  // 드로워 하단 버튼 스타일
  .drawer-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #f0f0f0;
    display: flex;
    flex-direction: row;  // 명시적으로 가로 배치
    justify-content: flex-end;
    align-items: center;  // 세로 중앙 정렬
    gap: 8px;

    // 버튼 크기 통일
    .ant-btn {
      min-width: 80px;
    }
  }

  :deep(.yellow-button) {
    background-color: #FFCC32 !important;
    border-color: #FFCC32 !important;
    color: #000 !important;

    &:hover {
      background-color: #E6B82E !important;
      border-color: #E6B82E !important;
    }
  }
}
</style>